import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const DEFAULT_CANDIDATE_FILTERS = {
  consumer_name: 'support_context',
  permission_scope: 'supporting_context',
  source_type: '',
  source_key: '',
  reviewed_state: '',
  lifecycle_state: '',
  freshness_state: '',
  include_ineligible: false,
};

const DEFAULT_ELIGIBILITY_FILTERS = {
  safe_summary_id: '',
  consumer_name: '',
  eligibility_state: '',
  permission_scope: '',
};

const PERMISSION_FLAG_FIELDS = [
  'can_serve_consumer',
  'can_influence_answer',
  'can_be_used_as_final_answer',
  'can_trigger_action',
  'can_generate_tests',
  'can_generate_automation',
  'can_rank_impact',
  'can_support_rca',
];

function cleanFilters(filters) {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (typeof value === 'boolean') {
      acc[key] = value;
    } else if (value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});
}

function formatList(value) {
  if (!Array.isArray(value) || value.length === 0) {
    return 'None';
  }
  return value.join(', ');
}

function formatBool(value) {
  return value ? 'true' : 'false';
}

function formatDate(value) {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function safeItems(response) {
  return Array.isArray(response?.items) ? response.items : [];
}

function errorMessage(err) {
  if (err?.status === 403) {
    return 'Admin access required.';
  }
  return err?.status ? `Request failed (${err.status}).` : 'Request failed.';
}

function FiltersGrid({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(160px, 1fr))' },
        mb: 2,
      }}
    >
      {children}
    </Box>
  );
}

function CandidateFilters({ filters, onChange }) {
  const setField = (field, value) => onChange({ ...filters, [field]: value });

  return (
    <FiltersGrid>
      <TextField
        label="Consumer name"
        size="small"
        value={filters.consumer_name}
        onChange={(event) => setField('consumer_name', event.target.value)}
      />
      <TextField
        label="Permission scope"
        size="small"
        value={filters.permission_scope}
        onChange={(event) => setField('permission_scope', event.target.value)}
      />
      <TextField
        label="Source type"
        size="small"
        value={filters.source_type}
        onChange={(event) => setField('source_type', event.target.value)}
      />
      <TextField
        label="Source key"
        size="small"
        value={filters.source_key}
        onChange={(event) => setField('source_key', event.target.value)}
      />
      <TextField
        label="Reviewed state"
        size="small"
        value={filters.reviewed_state}
        onChange={(event) => setField('reviewed_state', event.target.value)}
      />
      <TextField
        label="Lifecycle state"
        size="small"
        value={filters.lifecycle_state}
        onChange={(event) => setField('lifecycle_state', event.target.value)}
      />
      <TextField
        label="Freshness state"
        size="small"
        value={filters.freshness_state}
        onChange={(event) => setField('freshness_state', event.target.value)}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={filters.include_ineligible}
            onChange={(event) => setField('include_ineligible', event.target.checked)}
          />
        }
        label="Include ineligible"
      />
    </FiltersGrid>
  );
}

function EligibilityFilters({ filters, onChange }) {
  const setField = (field, value) => onChange({ ...filters, [field]: value });

  return (
    <FiltersGrid>
      <TextField
        label="Safe summary ID"
        size="small"
        value={filters.safe_summary_id}
        onChange={(event) => setField('safe_summary_id', event.target.value)}
      />
      <TextField
        label="Consumer name"
        size="small"
        value={filters.consumer_name}
        onChange={(event) => setField('consumer_name', event.target.value)}
      />
      <TextField
        label="Eligibility state"
        size="small"
        value={filters.eligibility_state}
        onChange={(event) => setField('eligibility_state', event.target.value)}
      />
      <TextField
        label="Permission scope"
        size="small"
        value={filters.permission_scope}
        onChange={(event) => setField('permission_scope', event.target.value)}
      />
    </FiltersGrid>
  );
}

function CandidatesTable({ rows }) {
  if (rows.length === 0) {
    return (
      <Alert severity="info" data-testid="safe-summary-eligibility-candidates-empty">
        No safe summary eligibility candidates found.
      </Alert>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" data-testid="safe-summary-eligibility-candidates-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Safe title</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>States</TableCell>
            <TableCell>Gate</TableCell>
            <TableCell>Reason codes</TableCell>
            <TableCell>Existing eligibility</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.safe_summary_id || `${row.source_type}-${row.source_key}`}>
              <TableCell>{row.safe_title || '-'}</TableCell>
              <TableCell>
                <Typography variant="body2">{row.source_type || '-'}</Typography>
                <Typography variant="caption" color="text.secondary">{row.source_key || '-'}</Typography>
              </TableCell>
              <TableCell>
                <Stack spacing={0.5}>
                  <Typography variant="caption">Reviewed: {row.reviewed_state || '-'}</Typography>
                  <Typography variant="caption">Lifecycle: {row.lifecycle_state || '-'}</Typography>
                  <Typography variant="caption">Freshness: {row.freshness_state || '-'}</Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={row.gate_passed ? 'success' : 'warning'}
                  label={row.gate_passed ? 'passed' : 'blocked'}
                />
              </TableCell>
              <TableCell>{formatList(row.gate_reason_codes)}</TableCell>
              <TableCell>
                <Typography variant="body2">{row.existing_eligibility_count ?? 0}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatList(row.existing_eligibility_states)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PermissionFlags({ row }) {
  return (
    <Stack spacing={0.5}>
      {PERMISSION_FLAG_FIELDS.map((field) => (
        <Typography key={field} variant="caption">
          {field}: {formatBool(row[field])}
        </Typography>
      ))}
    </Stack>
  );
}

function EligibilityRowsTable({ rows }) {
  if (rows.length === 0) {
    return (
      <Alert severity="info" data-testid="safe-summary-eligibility-rows-empty">
        No safe summary eligibility rows found.
      </Alert>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" data-testid="safe-summary-eligibility-rows-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Consumer</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Caveats</TableCell>
            <TableCell>Permission flags</TableCell>
            <TableCell>Review timestamps</TableCell>
            <TableCell>Reason codes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.eligibility_id || row.safe_summary_id}>
              <TableCell>
                <Typography variant="body2">{row.consumer_name || '-'}</Typography>
                <Typography variant="caption" color="text.secondary">{row.permission_scope || '-'}</Typography>
              </TableCell>
              <TableCell>{row.eligibility_state || '-'}</TableCell>
              <TableCell>
                <Typography variant="body2">{row.source_type || '-'}</Typography>
                <Typography variant="caption" color="text.secondary">{row.source_key || '-'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption">Caveat required: {formatBool(row.caveat_required)}</Typography>
                <br />
                <Typography variant="caption">Human review: {formatBool(row.requires_human_review)}</Typography>
              </TableCell>
              <TableCell>
                <PermissionFlags row={row} />
              </TableCell>
              <TableCell>
                <Typography variant="caption">Reviewed: {formatDate(row.reviewed_at)}</Typography>
                <br />
                <Typography variant="caption">Revoked: {formatDate(row.revoked_at)}</Typography>
              </TableCell>
              <TableCell>{formatList(row.eligibility_reason_codes)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SafetyStatusPanel() {
  return (
    <Paper variant="outlined" sx={{ p: 3 }} data-testid="safe-summary-eligibility-safety-status">
      <Stack spacing={1.5}>
        <Typography variant="h6">Safety Status</Typography>
        {[
          'Read-only view',
          'Production mutation routes are disabled',
          'No consumer activation',
          'No KCE bridge',
          'Supporting-context eligibility is not serving',
          'Final-answer/action/generation permissions are unavailable',
        ].map((item) => (
          <Chip key={item} label={item} variant="outlined" />
        ))}
      </Stack>
    </Paper>
  );
}

const SafeSummaryEligibilityAdmin = ({ selectedProject, authenticatedUser, apiClient }) => {
  const projectId = selectedProject?.project_id;
  const isAdmin = authenticatedUser?.role === 'project_admin' || authenticatedUser?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState('candidates');
  const [candidateFilters, setCandidateFilters] = useState(DEFAULT_CANDIDATE_FILTERS);
  const [eligibilityFilters, setEligibilityFilters] = useState(DEFAULT_ELIGIBILITY_FILTERS);
  const [candidates, setCandidates] = useState([]);
  const [eligibilities, setEligibilities] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [eligibilitiesLoading, setEligibilitiesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState(null);
  const [eligibilitiesError, setEligibilitiesError] = useState(null);

  const candidateQuery = useMemo(() => cleanFilters(candidateFilters), [candidateFilters]);
  const eligibilityQuery = useMemo(() => cleanFilters(eligibilityFilters), [eligibilityFilters]);

  const loadCandidates = useCallback(async () => {
    if (!projectId || !apiClient?.listSafeSummaryEligibilityCandidates) {
      setCandidates([]);
      return;
    }
    setCandidatesLoading(true);
    setCandidatesError(null);
    try {
      const response = await apiClient.listSafeSummaryEligibilityCandidates(projectId, candidateQuery);
      setCandidates(safeItems(response));
    } catch (err) {
      setCandidates([]);
      setCandidatesError(errorMessage(err));
    } finally {
      setCandidatesLoading(false);
    }
  }, [projectId, apiClient, candidateQuery]);

  const loadEligibilities = useCallback(async () => {
    if (!projectId || !apiClient?.listSafeSummaryEligibilities) {
      setEligibilities([]);
      return;
    }
    setEligibilitiesLoading(true);
    setEligibilitiesError(null);
    try {
      const response = await apiClient.listSafeSummaryEligibilities(projectId, eligibilityQuery);
      setEligibilities(safeItems(response));
    } catch (err) {
      setEligibilities([]);
      setEligibilitiesError(errorMessage(err));
    } finally {
      setEligibilitiesLoading(false);
    }
  }, [projectId, apiClient, eligibilityQuery]);

  useEffect(() => {
    setCandidates([]);
    setEligibilities([]);
    setCandidatesError(null);
    setEligibilitiesError(null);
  }, [projectId]);

  useEffect(() => {
    if (activeTab === 'candidates') {
      loadCandidates();
    }
  }, [activeTab, loadCandidates]);

  useEffect(() => {
    if (activeTab === 'eligibilityRows') {
      loadEligibilities();
    }
  }, [activeTab, loadEligibilities]);

  if (!projectId) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }} data-testid="safe-summary-eligibility-page">
        <Typography>Select a project to inspect safe summary eligibility.</Typography>
      </Paper>
    );
  }

  if (!isAdmin) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }} data-testid="safe-summary-eligibility-page">
        <Alert severity="warning" data-testid="safe-summary-eligibility-access-denied">
          Admin access required.
        </Alert>
      </Paper>
    );
  }

  return (
    <Box data-testid="safe-summary-eligibility-page" sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Safe Summary Eligibility
          </Typography>
          <Typography color="text.secondary">
            Read-only admin view for candidate gates, existing eligibility rows, provenance, and caveats.
          </Typography>
        </Box>

        <SafetyStatusPanel />

        <Paper variant="outlined">
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            aria-label="Safe summary eligibility views"
          >
            <Tab value="candidates" label="Candidates" />
            <Tab value="eligibilityRows" label="Eligibility Rows" />
            <Tab value="safetyStatus" label="Safety Status" />
          </Tabs>
        </Paper>

        {activeTab === 'candidates' && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Candidates</Typography>
              <CandidateFilters filters={candidateFilters} onChange={setCandidateFilters} />
              {candidatesLoading && <CircularProgress size={24} aria-label="Loading candidates" />}
              {candidatesError && <Alert severity="error">{candidatesError}</Alert>}
              {!candidatesLoading && !candidatesError && <CandidatesTable rows={candidates} />}
            </Stack>
          </Paper>
        )}

        {activeTab === 'eligibilityRows' && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Eligibility Rows</Typography>
              <EligibilityFilters filters={eligibilityFilters} onChange={setEligibilityFilters} />
              {eligibilitiesLoading && <CircularProgress size={24} aria-label="Loading eligibility rows" />}
              {eligibilitiesError && <Alert severity="error">{eligibilitiesError}</Alert>}
              {!eligibilitiesLoading && !eligibilitiesError && <EligibilityRowsTable rows={eligibilities} />}
            </Stack>
          </Paper>
        )}

        {activeTab === 'safetyStatus' && <SafetyStatusPanel />}
      </Stack>
    </Box>
  );
};

export default SafeSummaryEligibilityAdmin;
